import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Auth } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";
import { apiErrorMessage } from "@/api/client";

export default function Login() {
  const nav = useNavigate();
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice ?? null;
  const { token, setSession } = useAuthStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (token) return <Navigate to="/" replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const r = await Auth.login(email, password);
      // We use the token to fetch the user once.
      const me = await (async () => {
        const { api } = await import("@/api/client");
        const { data } = await api.get("/auth/me", {
          headers: { Authorization: `Bearer ${r.access_token}` },
        });
        return data;
      })();
      setSession(r.access_token, me);
      nav("/pipeline", { replace: true });
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-md panel p-8">
        <div className="flex items-center gap-3 mb-6">
          <img
            src="/favicon.svg"
            alt="LeadZeppelin"
            width={36}
            height={36}
            className="w-9 h-9 rounded-lg shadow-[0_6px_20px_rgba(94,176,255,0.28)]"
          />
          <div>
            <div className="text-lg font-semibold">LeadZeppelin</div>
            <div className="text-sm text-mut-300">by Techmellon</div>
          </div>
        </div>

        <h1 className="text-xl font-semibold mb-1">Sign in</h1>
        <p className="text-mut-300 text-md mb-6">Internal pipeline & opportunity tracker.</p>

        {notice && <div className="mb-4 text-sm text-accent-azure">{notice}</div>}

        <label className="label">Email</label>
        <input
          className="input mb-3"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <label className="label">Password</label>
        <input
          className="input mb-4"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        {err && <div className="mb-3 text-sm text-accent-pink">{err}</div>}

        <button type="submit" className="btn btn-primary w-full justify-center" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>

        <div className="mt-3 text-center">
          <button
            type="button"
            className="text-sm text-accent-azure hover:underline"
            onClick={() => nav("/forgot-password")}
          >
            Forgot password?
          </button>
        </div>

        <p className="text-xs text-mut-500 mt-5 leading-relaxed">
          For team access, ask an admin to add you in <span className="kbd">Settings → Team</span>.
        </p>
      </form>
    </div>
  );
}
